import { useState, useEffect } from 'react';
import {
    Chip,
    Collapse,
    Grid,
    Typography,
    Box,
    IconButton,
    Button,
    Paper,
    CircularProgress,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormControlLabel,
    Checkbox,
} from '@mui/material';
import { useSelector } from 'react-redux';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import FormTextField from '../../components/FormFields/FormTextField';
import { selectCurrentAccessToken, selectCurrentUser } from '../../features/authentication/authSlice';
import fetchData from './adminUtils/fetchData';
import handleDelete from './adminUtils/handleDelete';
import handleUpdate from './adminUtils/handleUpdate';
import { roles } from '../../config/roles';
import UpdateButton from './adminUtils/updateButton';
import SearchBar from './adminUtils/SearchBar';
import PaginationInfo from './PaginationInfo';
import { useAddNewLokiMutation } from '../../api/lokiSlice';
import DeleteUserDialog from './DeleteUserDialog';

function UserList({ fetchPeopleData, onSuccess, onError, setSuccessMessage, setErrorMessage }) {
    const [selectedUser, setSelectedUser] = useState(null);
    const [users, setUsers] = useState([]);
    const [newUserData, setNewUserData] = useState({ username: '', name: '', titteli: '', role: '', password: '' });
    const [addUserData, setAddUserData] = useState({ username: '', name: '', titteli: '', role: '', password: '' });
    const [showAddForm, setShowAddForm] = useState(false);
    const [createPersonToo, setCreatePersonToo] = useState(false);
    const [selectedRole, setSelectedRole] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;
    const [sendLog] = useAddNewLokiMutation();
    const accessToken = useSelector(selectCurrentAccessToken);
    const { name: currentUserName } = useSelector(selectCurrentUser);
    const userRoles = Object.values(roles);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [userToDelete, setUserToDelete] = useState(null);

    const [searchTerm, setSearchTerm] = useState('');

    const handleSearchChange = (newSearchTerm) => {
        setSearchTerm(newSearchTerm);
        setCurrentPage(1);
    };

    const filteredUsers = Array.isArray(users)
        ? users.filter((user) =>
            user.name.toLowerCase().includes(searchTerm.toLowerCase()) &&
            (selectedRole === '' || user.role === selectedRole)
        )
        : [];
    const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const displayedUsers = filteredUsers.slice(startIndex, endIndex);

    const handleNextPage = () => {
        if (currentPage < totalPages) setCurrentPage(currentPage + 1);
    };

    const handlePreviousPage = () => {
        if (currentPage > 1) setCurrentPage(currentPage - 1);
    };

    const handleUserClick = (user) => {
        setShowAddForm(false);
        if (selectedUser?.id === user.id) {
            setSelectedUser(null);
            setNewUserData({ username: '', name: '', titteli: '', role: '', password: '' });
        } else {
            setSelectedUser(user);
            setNewUserData(user);
        }
    };

    const handleAddClick = () => {
        setSelectedUser(null);
        setNewUserData({ username: '', name: '', titteli: '', role: '', password: '' });
        setShowAddForm(!showAddForm);
    };

    const fetchUsers = () => {
        fetchData('users', accessToken, setUsers);
    };

    const translateUserErrorMessage = (message) => {
        if (!message) {
            return 'Käyttäjän luominen epäonnistui. Tarkista tiedot ja yritä uudelleen.';
        }

        const normalizedMessage = message.toLowerCase();

        if (normalizedMessage.includes('duplicate username')) {
            return 'Käyttäjätunnus on jo käytössä. Valitse toinen sähköposti tai käyttäjätunnus.';
        }

        if (normalizedMessage.includes('must have a username')) {
            return 'Käyttäjätunnus puuttuu. Lisää käyttäjälle sähköposti tai käyttäjätunnus.';
        }

        if (normalizedMessage.includes('must have a name')) {
            return 'Nimi puuttuu. Lisää käyttäjälle nimi ennen tallentamista.';
        }

        if (normalizedMessage.includes('must have a password')) {
            return 'Salasana puuttuu. Lisää käyttäjälle salasana ennen tallentamista.';
        }

        if (normalizedMessage.includes('token')) {
            return 'Istunto ei ole enää voimassa. Kirjaudu uudelleen ja yritä sitten uudestaan.';
        }

        return 'Käyttäjän luominen epäonnistui. Tarkista tiedot ja yritä uudelleen.';
    };

    const formatApiError = (payload, fallbackMessage) => {
        if (!payload) {
            return fallbackMessage;
        }

        if (typeof payload.error === 'string') {
            return translateUserErrorMessage(payload.error);
        }

        if (payload.error && typeof payload.error === 'object') {
            return Object.values(payload.error)
                .map((message) => translateUserErrorMessage(message))
                .join(' ');
        }

        if (typeof payload.message === 'string') {
            return translateUserErrorMessage(payload.message);
        }

        return fallbackMessage;
    };

    useEffect(() => {
        fetchUsers();
    }, []);

    const deleteUserFilter = (user) => setUsers(users.filter((u) => u.id !== user.id));

    const handleLisääUser = async (user) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/users`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${accessToken}`,
                },
                body: JSON.stringify({
                    username: user.username,
                    password: user.password,
                    name: user.name,
                    role: user.role,
                    titteli: user.titteli,
                    addHenkilo: createPersonToo,
                }),
            });

            const payload = await response.json().catch(() => null);

            if (!response.ok) {
                const errorMessage = formatApiError(payload, 'Käyttäjän luominen epäonnistui');
                onError(errorMessage);
                throw new Error(errorMessage);
            }

            onSuccess('Käyttäjä lisätty onnistuneesti.', 'users', payload.id);
            setAddUserData({ username: '', name: '', titteli: '', role: '', password: '' });
            setShowAddForm(false);
            fetchUsers();
            fetchPeopleData();
        } catch (err) {
            console.log(err);
        }
    };

    const handleDeleteUserClick = (event, user) => {
        event.stopPropagation();
        setUserToDelete(user);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (!userToDelete) return;

        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/users`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${accessToken}`
                },
                body: JSON.stringify({
                    id: userToDelete.id
                })
            });

            if (!response.ok) {
                setErrorMessage('Käyttäjän poistaminen epäonnistui');
                throw new Error('Failed to delete user');
            }

            deleteUserFilter(userToDelete);
            setSuccessMessage('Käyttäjä poistettu onnistuneesti!');

            // Clear selected user if it was the deleted one
            if (selectedUser?.id === userToDelete.id) {
                setSelectedUser(null);
                setNewUserData({ username: '', name: '', titteli: '', role: '', password: '' });
            }

            fetchUsers();
        } catch (error) {
            console.error(error);
        }
    };

    const updateUsersFilter = (updatedUser, responseData) => {
        setUsers((prevUsers) =>
            prevUsers.map((user) => (user.id === updatedUser.id ? responseData : user))
        );
    };

    const handleUpdateUser = (user) => {
        handleUpdate(
            'users',
            accessToken,
            user,
            updateUsersFilter,
            setSuccessMessage,
            setErrorMessage,
            sendLog,
            currentUserName
        );
    };

    return (
        <Box sx={{ py: 3 }}>
            <Paper elevation={2} sx={{ p: 3, borderRadius: 2, bgcolor: 'background.paper' }}>
                {/* Header and Search */}
                <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={4}>
                        <Typography variant="h6" sx={{ fontWeight: 500 }}>
                            Käyttäjät
                        </Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <SearchBar searchTerm={searchTerm} onSearchChange={handleSearchChange} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <FormControl sx={{ width: '100%' }}>
                            <InputLabel id="role-filter-label">Rooli</InputLabel>
                            <Select
                                labelId="role-filter-label"
                                id="role-filter-select"
                                value={selectedRole}
                                label="Rooli"
                                onChange={(event) => {
                                    setSelectedRole(event.target.value);
                                    setCurrentPage(1);
                                }}
                            >
                                <MenuItem value="">Kaikki roolit</MenuItem>
                                {userRoles.map((role) => (
                                    <MenuItem key={role} value={role}>
                                        {role}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>

                {/* Users List */}
                {displayedUsers.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ my: 2 }}>
                        Ei käyttäjiä näytettäväksi.
                    </Typography>
                ) : (
                    displayedUsers.map((user) => {
                        const isSelected = selectedUser?.id === user.id;
                        const summaryText = [user.username, user.titteli].filter(Boolean).join(' | ');

                        return (
                            <Box key={user.id} sx={{ mb: 1.5 }}>
                                <Box
                                    sx={{
                                        p: 2,
                                        borderRadius: 2,
                                        border: '1px solid',
                                        borderColor: isSelected ? 'taphaBlue.main' : 'grey.200',
                                        bgcolor: isSelected ? 'rgba(25, 118, 210, 0.06)' : 'taphaWhite.main',
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        justifyContent: 'space-between',
                                        gap: 2,
                                        '&:hover': {
                                            bgcolor: isSelected ? 'rgba(25, 118, 210, 0.08)' : 'grey.50',
                                            boxShadow: '0 4px 10px rgba(0,0,0,0.08)',
                                            cursor: 'pointer',
                                        },
                                        transition: 'all 0.2s ease',
                                    }}
                                    onClick={() => handleUserClick(user)}
                                >
                                    <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                                        <Typography variant="body1" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' }, fontWeight: 600 }}>
                                            {user.name}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                                            {summaryText || 'Ei lisätietoja'}
                                        </Typography>
                                        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 1 }}>
                                            {user.role && <Chip size="small" label={user.role} />}
                                        </Box>
                                    </Box>
                                    <IconButton
                                        onClick={(event) => handleDeleteUserClick(event, user)}
                                        sx={{ color: 'error.main', flexShrink: 0 }}
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </Box>

                                <Collapse in={isSelected} timeout="auto" unmountOnExit>
                                    <Box sx={{ mt: 1, p: 2, border: '1px solid', borderColor: 'grey.200', borderRadius: 2, bgcolor: 'grey.50' }}>
                                        <Typography variant="subtitle1" sx={{ mb: 2 }}>
                                            Muokkaa käyttäjää
                                        </Typography>
                                        <Grid container spacing={2} alignItems="center">
                                            <Grid item xs={12} sm={8}>
                                                <FormTextField
                                                    fieldName="username"
                                                    label="Käyttäjätunnus"
                                                    multiline
                                                    fullWidth
                                                    value={newUserData.username}
                                                    onChange={(event) =>
                                                        setNewUserData((prev) => ({ ...prev, username: event.target.value }))
                                                    }
                                                    sx={{ mb: 2 }}
                                                />
                                                <FormTextField
                                                    fieldName="name"
                                                    label="Nimi"
                                                    multiline
                                                    fullWidth
                                                    value={newUserData.name}
                                                    onChange={(event) =>
                                                        setNewUserData((prev) => ({ ...prev, name: event.target.value }))
                                                    }
                                                    sx={{ mb: 2 }}
                                                />
                                                <FormControl sx={{ width: '100%', mb: 2 }}>
                                                    <InputLabel id="role-label">Rooli</InputLabel>
                                                    <Select
                                                        labelId="role-label"
                                                        id="role-select"
                                                        value={newUserData.role}
                                                        label="Rooli"
                                                        onChange={(event) =>
                                                            setNewUserData((prev) => ({ ...prev, role: event.target.value }))
                                                        }
                                                    >
                                                        {userRoles.map((role) => (
                                                            <MenuItem key={role} value={role}>
                                                                {role}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                                <FormTextField
                                                    fieldName="titteli"
                                                    label="Titteli"
                                                    multiline
                                                    fullWidth
                                                    value={newUserData.titteli}
                                                    onChange={(event) =>
                                                        setNewUserData((prev) => ({ ...prev, titteli: event.target.value }))
                                                    }
                                                    sx={{ mb: 2 }}
                                                />
                                                <FormTextField
                                                    fieldName="password"
                                                    label="Salasana"
                                                    multiline
                                                    fullWidth
                                                    value={newUserData.password || ''}
                                                    onChange={(event) =>
                                                        setNewUserData((prev) => ({ ...prev, password: event.target.value }))
                                                    }
                                                    sx={{ mb: 2 }}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={4}>
                                                <UpdateButton handleUpdate={handleUpdateUser} data={newUserData} />
                                            </Grid>
                                        </Grid>
                                    </Box>
                                </Collapse>
                            </Box>
                        );
                    })
                )}

                {/* Add Form */}
                <Box sx={{ mt: 3 }}>
                    <Button
                        variant="contained"
                        startIcon={showAddForm ? <CloseIcon /> : <AddIcon />}
                        onClick={handleAddClick}
                        sx={{
                            bgcolor: showAddForm ? 'grey.600' : 'taphaBlue.main',
                            '&:hover': { bgcolor: showAddForm ? 'grey.700' : 'taphaBlue.dark' },
                        }}
                    >
                        {showAddForm ? 'Sulje' : 'Lisää käyttäjä'}
                    </Button>
                    {showAddForm && (
                        <Box
                            sx={{
                                mt: 2,
                                p: 2,
                                border: '1px solid',
                                borderColor: 'grey.200',
                                borderRadius: 1,
                                bgcolor: 'grey.50',
                            }}
                        >
                            <Typography variant="subtitle1" sx={{ mb: 2 }}>
                                Lisää uusi käyttäjä
                            </Typography>
                            <Grid container spacing={2} alignItems="center">
                                <Grid item xs={12} sm={8}>
                                    <FormTextField
                                        key="addUserUsername"
                                        fieldName="username"
                                        label="Käyttäjätunnus"
                                        multiline
                                        fullWidth
                                        value={addUserData.username}
                                        onChange={(event) =>
                                            setAddUserData((prev) => ({ ...prev, username: event.target.value }))
                                        }
                                        sx={{ mb: 2 }}
                                    />
                                    <FormTextField
                                        key="addUserName"
                                        fieldName="name"
                                        label="Nimi"
                                        multiline
                                        fullWidth
                                        value={addUserData.name}
                                        onChange={(event) =>
                                            setAddUserData((prev) => ({ ...prev, name: event.target.value }))
                                        }
                                        sx={{ mb: 2 }}
                                    />
                                    <FormControl sx={{ width: '100%', mb: 2 }}>
                                        <InputLabel id="add-role-label">Rooli</InputLabel>
                                        <Select
                                            labelId="add-role-label"
                                            id="add-role-select"
                                            value={addUserData.role}
                                            label="Rooli"
                                            onChange={(event) =>
                                                setAddUserData((prev) => ({ ...prev, role: event.target.value }))
                                            }
                                        >
                                            {userRoles.map((role) => (
                                                <MenuItem key={role} value={role}>
                                                    {role}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    <FormTextField
                                        key="addUserTitteli"
                                        fieldName="titteli"
                                        label="Titteli"
                                        multiline
                                        fullWidth
                                        value={addUserData.titteli}
                                        onChange={(event) =>
                                            setAddUserData((prev) => ({ ...prev, titteli: event.target.value }))
                                        }
                                        sx={{ mb: 2 }}
                                    />
                                    <FormTextField
                                        key="addUserPassword"
                                        fieldName="password"
                                        label="Salasana"
                                        multiline
                                        fullWidth
                                        value={addUserData.password}
                                        onChange={(event) =>
                                            setAddUserData((prev) => ({ ...prev, password: event.target.value }))
                                        }
                                        sx={{ mb: 2 }}
                                    />
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={createPersonToo}
                                                onChange={(event) => setCreatePersonToo(event.target.checked)}
                                                name="createPersonToo"
                                                color="primary"
                                            />
                                        }
                                        label="Luo myös henkilö"
                                        sx={{ mb: 2 }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <Button
                                        variant="contained"
                                        size="small"
                                        startIcon={ <AddIcon />}
                                        onClick={() => handleLisääUser(addUserData)}
                                        sx={{ bgcolor: 'taphaGreen.main', '&:hover': { bgcolor: 'taphaGreen.dark' } }}
                                    >
                                       Lisää
                                    </Button>
                                </Grid>
                            </Grid>
                        </Box>
                    )}
                </Box>

                {/* Pagination */}
                {filteredUsers.length > itemsPerPage && (
                    <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <PaginationInfo
                            currentPage={currentPage}
                            itemsPerPage={itemsPerPage}
                            filteredArray={filteredUsers}
                            array={users}
                        />
                        <Box sx={{ mt: 1 }}>
                            <IconButton
                                size="small"
                                onClick={handlePreviousPage}
                                disabled={currentPage <= 1}
                                sx={{ mr: 1 }}
                            >
                                <ArrowBackIcon />
                            </IconButton>
                            <IconButton
                                size="small"
                                onClick={handleNextPage}
                                disabled={currentPage >= totalPages}
                            >
                                <ArrowForwardIcon />
                            </IconButton>
                        </Box>
                    </Box>
                )}
            </Paper>

            {/* Delete User Dialog */}
            <DeleteUserDialog
                open={deleteDialogOpen}
                onClose={() => {
                    setDeleteDialogOpen(false);
                    setUserToDelete(null);
                }}
                user={userToDelete}
                allUsers={users}
                onDeleteConfirm={handleDeleteConfirm}
            />
        </Box>
    );
}

export default UserList;